import { useState } from "react";
import { postApi } from "../api/API";
import {
  getActionsByCategory,
  buildActionUrl,
} from "../config/dashboard-actions";
import { executeAction } from "../lib/dashboardUtils";
import { OPERATIONS_URL, TICKETING_URL } from "../config/urls";

export const useDashboardActions = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredActions, setFilteredActions] = useState<any[]>([]);

  // Get all action categories
  const verificationActions = getActionsByCategory("verification");
  const userActions = getActionsByCategory("user");
  const rentOrderActions = getActionsByCategory("rent_orders");
  const buyOrderActions = getActionsByCategory("buy_orders");

  // Search functionality with action execution
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.currentTarget.value;
    setSearchQuery(query);

    if (query.trim() === "") {
      setFilteredActions([]);
      return;
    }

    const allActions = [
      ...getActionsByCategory("user"),
      ...getActionsByCategory("rent_orders"),
      ...getActionsByCategory("buy_orders"),
      ...getActionsByCategory("verification"),
    ];

    const filtered = allActions.filter(
      (action) =>
        action.title.toLowerCase().includes(query.toLowerCase()) ||
        action.description.toLowerCase().includes(query.toLowerCase())
    );

    setFilteredActions(filtered);
  };

  const handleActionClick = (action: any, actionParams: any) => {
    if (action.category === "verification") {
      const actionUrl = buildActionUrl(action, actionParams);
      window.open(actionUrl, "_blank", "noopener,noreferrer");
      setSearchQuery("");
      setFilteredActions([]);
      return;
    }

    if (action.requiresUserId && !actionParams.userId) {
      alert("This action requires a user to be selected first.");
      return;
    }

    if (
      action.requiresPhoneNumber &&
      (!actionParams.phoneNumber || !actionParams.countryCode)
    ) {
      alert("This action requires phone number information.");
      return;
    }

    const actionUrl = buildActionUrl(action, actionParams);
    window.open(actionUrl, "_blank", "noopener,noreferrer");
    setSearchQuery("");
    setFilteredActions([]);
  };

  // Login to Operations handler
  const handleOperationsLogin = () => {
    const token =
      localStorage.getItem("authToken") || localStorage.getItem("token") || "";
    // const operationsUrl = 'https://operations.sharepal.co.in';
    const operationsUrl = OPERATIONS_URL || "https://operations.sharepal.co.in";
    const finalUrl = `${operationsUrl}/?token=${token}`;
    window.open(finalUrl, "_blank", "noopener,noreferrer");
  };

  // Login to Rent handler using proper API
  const handleRentLogin = async (userDetails: any) => {
    if (
      !userDetails?.user?.calling_number ||
      !userDetails?.user?.country_code_calling
    ) {
      alert("User phone number is required for rent login");
      return;
    }

    try {
      // First, create backend order if user ID is provided
      if (userDetails.user.id) {
        await postApi({
          url: "/order/backend",
          body: {
            calling_number_code: userDetails.user.country_code_calling,
            calling_number: userDetails.user.calling_number,
            login_user_id: userDetails.user.id,
          },
          type: "new_admin",
        });
      }

      // Then create login request
      const data = await postApi<any>({
        url: "/backend/request",
        body: {
          calling_number: userDetails.user.calling_number,
          calling_number_code: userDetails.user.country_code_calling,
        },
        type: "new_admin",
      });

      // Open rent platform
      window.open(
        `https://sharepal.in/admin/${data.backend_login.unique_login_id}?token=${data.token}`,
        "_blank",
        "noopener,noreferrer"
      );
    } catch (error) {
      console.error("Error logging in to rent:", error);
      alert("Failed to login to rent platform. Please try again.");
    }
  };

  // Login to Ticketing handler
  const handleTicketingLogin = () => {
    const token =
      localStorage.getItem("authToken") || localStorage.getItem("token") || "";
    const ticketingUrl = TICKETING_URL || "https://ticketing.sharepal.co";
    const finalUrl = `${ticketingUrl}/?token=${token}`;
    window.open(finalUrl, "_blank", "noopener,noreferrer");
  };

  return {
    searchQuery,
    setSearchQuery,
    filteredActions,
    setFilteredActions,
    verificationActions,
    userActions,
    rentOrderActions,
    buyOrderActions,
    handleSearchChange,
    handleActionClick,
    handleOperationsLogin,
    handleRentLogin,
    handleTicketingLogin,
  };
};
